/**
 * Product-page media registry.
 *
 * product_info.yaml references the original assets (many shot on white, some
 * GIF demos, some UI screenshots). The dark layouts need to know what each
 * file IS so they can stage it correctly:
 *   kind: 'cutout'  transparent product render/cutout -> put on a dark stage
 *         'photo'   real photograph -> full/half bleed, object-fit cover
 *         'ui'      screenshot of software -> edge-to-edge tile, no padding
 *         'gif'     animated demo -> media band, cover, no frame
 *         'white'   product shot on a white background that has NOT been
 *                   re-cut yet -> layouts must never show it as a white box
 * `src` may point to a dark-ready replacement produced for that original.
 * `focal` is an object-position hint.  Optional extras a layout may use:
 *   stage: path of a 16:10 dark "staged" render (cutout on Deep Gray with
 *          spotlight + floor shadow) for heroes and cover slots
 *   stageM: 960x1200 portrait webp of the same stage (product centred) for mobile
 *   stageWebp: webp twin of `stage`
 *   video: { mp4, webm, poster } when a GIF has been re-encoded as a loop
 *   edge:  note when a cutout is sliced by its frame (has an alpha fade on that side)
 *   alt:   localized or plain alt text
 * Anything not listed falls back to a guess by extension.
 */
const D = '/assets/product/dark';
// transparent cutout (+ 16:10 staged render, its webp twin and a 960x1200 mobile stage)
const cut = (name, focal, extra) => ({
  src: `${D}/${name}_cut.png`, kind: 'cutout', focal,
  stage: `${D}/${name}_stage.jpg`, stageWebp: `${D}/${name}_stage.webp`, stageM: `${D}/${name}_stage_m.webp`,
  ...extra,
});
// GIF re-encoded as a muted loop; src is the poster frame
const gif = (name, focal) => ({
  src: `${D}/${name}_poster.jpg`, kind: 'gif', focal,
  video: { mp4: `${D}/${name}.mp4`, webm: `${D}/${name}.webm`, poster: `${D}/${name}_poster.jpg` },
});
const ui = (name, focal) => ({ src: `${D}/${name}_ui.webp`, kind: 'ui', focal });
const photo = (src, focal) => ({ src, kind: 'photo', focal });

// keys are the ORIGINAL yaml paths
const OVERRIDES = {
  // --- nxbot: welding cobot (beaver) ---
  '/assets/product/nxbot/beaver.jpg': cut('beaver', '50% 55%', { edge: 'table fades out at the bottom' }),
  '/assets/product/nxbot/beaver_f1.gif': gif('beaver_f1', '50% 50%'),
  '/assets/product/nxbot/beaver_f2.gif': gif('beaver_f2', '50% 50%'),
  '/assets/product/nxbot/beaver_f3.gif': gif('beaver_f3', '50% 50%'),
  '/assets/product/nxbot/welding_robot_logo.png': cut('welding_robot_logo', '50% 50%'),
  '/assets/product/nxbot/beaver_updated.png': cut('beaver_updated', '50% 50%', { edge: 'plate fades out bottom/left' }),
  // --- nxa: smart security (vs) ---
  '/assets/product/nxa/vs.png': cut('vs', '50% 45%'),
  '/assets/product/nxa/vs_logo.png': cut('vs_logo', '50% 45%'),
  '/assets/product/nxa/vs_cross.gif': gif('vs_cross', '50% 50%'),
  '/assets/product/nxa/vs_ilegal.gif': gif('vs_ilegal', '50% 50%'),
  '/assets/product/nxa/vs_sw.gif': gif('vs_sw', '50% 50%'),
  // --- nxa: parking (pms / pmsm) ---
  '/assets/product/nxa/pms.jpg': photo(`${D}/pms_photo.webp`, '45% 55%'),   // original is CMYK; RGB re-encode
  '/assets/product/nxa/pms.webp': photo(`${D}/pms_photo.webp`, '45% 55%'),
  '/assets/product/nxa/pms_rec.png': ui('pms_rec', '50% 50%'),
  '/assets/product/nxa/pms_all.png': ui('pms_all', '50% 50%'),
  // --- nxa: LPR engine (minij) ---
  '/assets/product/nxa/minij.png': cut('minij', '50% 50%'),
  '/assets/product/nxa/minij_logo.png': cut('minij_logo', '50% 50%'),
  '/assets/product/nxa/minij_ai.webp': photo(`${D}/minij_ai_photo.webp`, '50% 45%'),
  '/assets/product/nxa/minij_car.webp': ui('minij_car', '50% 50%'),
  // --- nxa: parking guidance (vpgs) ---
  '/assets/product/nxa/vpgs.webp': photo(`${D}/vpgs_photo.webp`, '50% 45%'),
  '/assets/product/nxa/vpgs.jpg': cut('vpgs_iso', '50% 50%', { edge: 'isometric illustration, floor feathered on 3 sides' }),
  '/assets/product/nxa/vpgs_ai.jpg': photo('/assets/product/nxa/vpgs_ai.jpg', '50% 50%'),
  '/assets/product/nxa/vpgs_logo.webp': cut('vpgs_logo', '50% 50%'),
  // --- nxa: parking pole / ilp ---
};

function guessKind(src) {
  const s = (src || '').toLowerCase();
  if (s.endsWith('.gif')) return 'gif';
  if (/_logo\.(png|webp)$/.test(s) || /collie\.png$|welding_robot_logo/.test(s)) return 'cutout';
  if (/pms_rec|pms_all|vs\.png$|_ai\.(webp|jpg)$|minij_car/.test(s)) return 'ui';
  if (/beaver\.jpg$|pms\.(jpg|webp)$|pmsm\.png$|minij\.png$|vpgs\.(webp|jpg)$|pp\.webp$|ilp\.png$/.test(s)) return 'white';
  return 'photo';
}

export function getMedia(src) {
  const o = OVERRIDES[src];
  if (o) return { src: o.src || src, kind: o.kind || guessKind(src), focal: o.focal || '50% 50%', alt: o.alt, stage: o.stage, stageM: o.stageM, stageWebp: o.stageWebp, video: o.video, edge: o.edge };
  return { src, kind: guessKind(src), focal: '50% 50%' };
}

export function registerMedia(map) {
  Object.assign(OVERRIDES, map);
}

export default getMedia;
